import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { MoreVertical, Edit, Trash2, History } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { VersionManagementDialog } from "./version-management-dialog.tsx";
import type { DocumentWithCounts } from "@shared/schema";

interface DocumentActionsMenuProps {
  document: DocumentWithCounts;
  onEdit?: (document: DocumentWithCounts) => void;
  onDelete?: (documentId: string) => void;
  onDeletingStateChange?: (isDeleting: boolean) => void;
  className?: string;
  variant?: "ghost" | "default" | "secondary" | "destructive" | "outline" | "link";
  size?: "default" | "sm" | "lg" | "icon";
}

export function DocumentActionsMenu({
  document,
  onEdit,
  onDelete,
  onDeletingStateChange,
  className = "",
  variant = "ghost",
  size = "icon"
}: DocumentActionsMenuProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [versionsDialogOpen, setVersionsDialogOpen] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Delete mutation with optimistic updates and error handling
  const deleteMutation = useMutation({
    mutationFn: async (documentId: string) => {
      onDeletingStateChange?.(true);
      return await apiRequest('DELETE', `/api/documents/${documentId}`);
    },
    onSuccess: () => {
      onDeletingStateChange?.(false);
      queryClient.invalidateQueries({ queryKey: ['/api/documents'] });
      toast({
        title: "Document deleted",
        description: "The document has been successfully deleted.",
      });
    },
    onError: (error: any) => {
      onDeletingStateChange?.(false);
      toast({
        title: "Error",
        description: error.message || "Failed to delete document",
        variant: "destructive",
      });
    },
  });

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onEdit) {
      onEdit(document);
    }
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteDialogOpen(true);
  };

  const handleVersionsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setVersionsDialogOpen(true);
  };

  const confirmDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteDialogOpen(false);
    
    if (onDelete) {
      onDelete(document.id);
    } else {
      // Use built-in mutation if no external handler provided
      deleteMutation.mutate(document.id);
    }
  };

  return (
    <>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button
            variant={variant}
            size={size}
            className={className}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <MoreVertical className="w-5 h-5" />
            <span className="sr-only">Document actions</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent 
          align="end"
        >
          {onEdit && (
            <DropdownMenuItem onClick={handleEdit}>
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={handleVersionsClick}>
            <History className="w-4 h-4 mr-2" />
            Versions
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={handleDeleteClick}
            className="text-destructive focus:text-destructive dark:text-red-400 dark:focus:text-red-300"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-background border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground flex items-center">
              <Trash2 className="w-5 h-5 mr-2 text-destructive dark:text-red-400" />
              Delete Document
            </AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              Are you sure you want to delete <span className="font-semibold text-foreground">"{document.name}"</span>?
            </AlertDialogDescription>
            <div className="mt-3 p-3 bg-destructive/10 dark:bg-red-950/30 border border-destructive/20 dark:border-red-800/30 rounded-md">
              <div className="text-sm font-medium text-destructive dark:text-red-400 mb-2">This action cannot be undone and will permanently remove:</div>
              <div className="space-y-1 text-sm text-muted-foreground">
                <div>• {document.pageCount || 0} pages of content</div>
                <div>• {document.currentVersion || 1} version{(document.currentVersion || 1) > 1 ? 's' : ''} of the document</div>
                <div>• All associated comments and annotations</div>
                <div>• All images and files from cloud storage</div>
              </div>
            </div>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              className="bg-background text-foreground border-border hover:bg-muted"
              onClick={(e) => e.stopPropagation()}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 dark:bg-red-600 dark:text-white dark:hover:bg-red-700"
            >
              Delete Document
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Version Management Dialog */}
      <VersionManagementDialog 
        isOpen={versionsDialogOpen}
        onClose={() => setVersionsDialogOpen(false)}
        document={document}
      />
    </>
  );
}